import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { MonthPriceCalendar } from '../components/MonthPriceCalendar';
import { Trophy, Wallet, Palmtree, Zap, PlaneTakeoff, ArrowRight, Globe2, Info, FlaskConical, SlidersHorizontal, Search, CalendarDays } from 'lucide-react';

const todayPlus = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };

export default function CountryResults() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const from = sp.get('from') || 'IN';
  const to = sp.get('to') || 'ID';
  const mode = sp.get('mode') || 'explore';
  const date = sp.get('date') || '';

  const [data, setData] = useState(null);
  const [meta, setMeta] = useState([]);
  const [filters, setFilters] = useState({ origin_region: '', origin_city: '', destination_region: '', destination_city: '' });
  const [regions, setRegions] = useState({ o: { regions: [], airports: [] }, d: { regions: [], airports: [] } });
  const [showCal, setShowCal] = useState(false);

  useEffect(() => { document.title = 'Country flights | FlightDeals.in'; api.countryMeta().then(({ data }) => setMeta(data.countries || [])).catch(() => {}); }, []);
  useEffect(() => {
    api.countryRegions(from).then(({ data }) => setRegions((r) => ({ ...r, o: data }))).catch(() => {});
    api.countryRegions(to).then(({ data }) => setRegions((r) => ({ ...r, d: data }))).catch(() => {});
  }, [from, to]);

  const load = useCallback(() => {
    setData(null);
    api.countrySearch({ origin_country: from, destination_country: to, mode, date: date || undefined, ...clean(filters), currency: 'INR' })
      .then(({ data }) => setData(data)).catch(() => setData({ results: [], groups: {}, coverage: {} }));
  }, [from, to, mode, date, filters]);
  useEffect(() => { load(); }, [load]);

  const cName = (cc) => { const c = meta.find((x) => x.country_code === cc); return c ? `${c.flag} ${c.name}` : cc; };

  const drill = (r) => {
    api.countryTrack('country_drilldown', data?.session_id, { route: r.route, origin: r.origin, destination: r.destination, is_mock: r.is_mock });
    // Use the SAME date the shown fare was found for (r.date → the explore/live date) so the
    // drill-down opens the flight that produced this price, not a generic 30-day-out date.
    const departDate = (mode === 'live' && date) ? date : (r.date || data?.date || todayPlus(30));
    const q = new URLSearchParams({ origin: r.origin, destination: r.destination, trip_type: 'one_way',
      depart_date: departDate, return_date: '', adults: 1, children: 0, cabin: 'economy' }).toString();
    navigate(`/flights/results?${q}`);
  };

  const drillDate = (r, day) => {
    api.countryTrack('country_calendar_pick', data?.session_id, { route: r.route, date: day });
    const q = new URLSearchParams({ origin: r.origin, destination: r.destination, trip_type: 'one_way',
      depart_date: day, return_date: '', adults: 1, children: 0, cabin: 'economy' }).toString();
    navigate(`/flights/results?${q}`);
  };

  const g = data?.groups || {};
  const cov = data?.coverage || {};

  return (
    <Layout>
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-2 text-primary-foreground/80 text-sm mb-1"><Globe2 className="h-4 w-4" /> Country Fare Explorer</div>
          <h1 className="font-display text-3xl font-semibold" data-testid="country-results-title">Flights from {cName(from)} to {cName(to)}</h1>
          <p className="text-primary-foreground/80 mt-1">Compare the cheapest airports and cities across both countries.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge className="rounded-full bg-white/15 text-primary-foreground">{mode === 'live' ? `Exact date · ${date}` : 'Explore · indicative'}</Badge>
            {data?.is_mock && (data?.count > 0) && <Badge className="rounded-full bg-amber-400/20 text-amber-100"><FlaskConical className="h-3 w-3 mr-1" />Demo data · is_mock</Badge>}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Coverage honesty banner */}
        {data && (
          <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-muted-foreground flex items-start gap-2 mb-5" data-testid="country-coverage">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
            <span>{cov.message || 'Best price we found.'} {cov.searched_pairs != null && <>Searched <strong>{cov.searched_pairs}</strong> of {cov.total_pairs} candidate airport pairs{cov.failed_pairs ? ` (${cov.failed_pairs} unavailable)` : ''}.</>} {data.indicative && ' Indicative fares — confirm live price on the next step.'}</span>
          </div>
        )}

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filters */}
          <aside className="lg:col-span-1 space-y-4">
            <Card className="p-4 rounded-2xl" data-testid="country-filters">
              <p className="font-semibold text-sm flex items-center gap-1.5 mb-3"><SlidersHorizontal className="h-4 w-4 text-accent" />Geographic filters</p>
              <FSelect label={`Origin region (${cName(from)})`} value={filters.origin_region} onChange={(v) => setFilters({ ...filters, origin_region: v })} options={regions.o.regions} testId="filter-origin-region" />
              <FSelect label="Destination region" value={filters.destination_region} onChange={(v) => setFilters({ ...filters, destination_region: v })} options={regions.d.regions} testId="filter-dest-region" />
              <FSelect label="Origin airport" value={filters.origin_city} onChange={(v) => setFilters({ ...filters, origin_city: v })} options={(regions.o.airports || []).map((a) => a.city)} testId="filter-origin-city" />
              <FSelect label="Destination airport" value={filters.destination_city} onChange={(v) => setFilters({ ...filters, destination_city: v })} options={(regions.d.airports || []).map((a) => a.city)} testId="filter-dest-city" />
              <Button variant="outline" className="w-full rounded-xl mt-1" onClick={() => setFilters({ origin_region: '', origin_city: '', destination_region: '', destination_city: '' })} data-testid="filter-reset">Reset filters</Button>
            </Card>
          </aside>

          {/* Results */}
          <main className="lg:col-span-3 space-y-6">
            {!data ? (
              <div className="grid gap-3 sm:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
            ) : (data.results || []).length === 0 ? (
              (data.provider_unavailable || cov.provider_unavailable) ? (
                <Card className="p-12 rounded-2xl text-center" data-testid="country-unavailable"><Info className="h-10 w-10 mx-auto text-amber-500 mb-2" /><p className="font-semibold">Couldn't fetch live fares right now</p><p className="text-sm text-muted-foreground mt-1">{cov.message || 'High demand — please try again in a moment.'}</p><Button className="rounded-xl mt-4" onClick={load} data-testid="country-retry">Try again</Button></Card>
              ) : (
                <Card className="p-12 rounded-2xl text-center" data-testid="country-empty"><Search className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No routes found</p><p className="text-sm text-muted-foreground">Try different countries, regions or an exact date.</p></Card>
              )
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="country-highlights">
                  <Highlight icon={Trophy} tone="emerald" label="Cheapest overall" item={g.cheapest_overall} onClick={drill} meta={meta} />
                  <Highlight icon={Wallet} tone="accent" label="Cheapest origin" item={(g.cheapest_by_origin_city || [])[0]} onClick={drill} meta={meta} />
                  <Highlight icon={Palmtree} tone="violet" label="Cheapest destination" item={(g.cheapest_by_destination_city || [])[0]} onClick={drill} meta={meta} />
                  <Highlight icon={Zap} tone="amber" label="Fastest" item={g.fastest} onClick={drill} meta={meta} showDur />
                  <Highlight icon={PlaneTakeoff} tone="blue" label="Best direct" item={g.best_direct} onClick={drill} meta={meta} />
                  <Highlight icon={Trophy} tone="emerald" label="Best value" item={g.best_value} onClick={drill} meta={meta} showScore />
                </div>

                {g.cheapest_overall && (
                  <div data-testid="country-flex-dates">
                    <button
                      type="button"
                      onClick={() => setShowCal((v) => !v)}
                      data-testid="country-flex-toggle"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline mb-2"
                    >
                      <CalendarDays className="h-4 w-4" />
                      {showCal ? 'Hide month calendar' : `See a whole month of prices for ${g.cheapest_overall.origin_city} → ${g.cheapest_overall.destination_city}`}
                    </button>
                    {showCal && (
                      <MonthPriceCalendar
                        origin={g.cheapest_overall.origin} destination={g.cheapest_overall.destination}
                        roundTrip={false} currency="INR"
                        initialMonth={(mode === 'live' && date) ? date.slice(0, 7) : undefined}
                        onPick={(day) => drillDate(g.cheapest_overall, day)}
                      />
                    )}
                  </div>
                )}

                <div>
                  <h2 className="font-semibold mb-2">Cheapest routes</h2>
                  <div className="space-y-2" data-testid="country-routes">
                    {data.results.map((r) => (
                      <Card key={r.route} className="p-4 rounded-2xl flex items-center gap-3 hover:border-accent/50 transition-colors" data-testid={`country-route-${r.route}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{r.origin_city} <ArrowRight className="h-3.5 w-3.5 inline text-muted-foreground" /> {r.destination_city}</span>
                            <Badge variant="outline" className="rounded-full text-[10px]">{r.route}</Badge>
                            <Badge variant="outline" className="rounded-full text-[10px]">{r.stops === 0 ? 'Direct' : `${r.stops} stop`}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{r.airline_name} · {r.origin_region} → {r.destination_region} · score {r.country_fare_score}/100 {r.is_mock ? '· indicative' : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-semibold tabular text-accent">{formatPrice(r.price, r.currency)}</p>
                          <p className="text-[10px] text-muted-foreground">best we found</p>
                        </div>
                        <Button onClick={() => drill(r)} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid={`country-drill-${r.route}`}>View flights <ArrowRight className="h-4 w-4 ml-1" /></Button>
                      </Card>
                    ))}
                  </div>
                </div>
                <AffiliateDisclosure variant="footer" />
              </>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
}

const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v));

const FSelect = ({ label, value, onChange, options, testId }) => (
  <div className="mb-3">
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</label>
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className="h-10" data-testid={testId}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent><SelectItem value="all">All</SelectItem>{[...new Set(options)].filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const TONE = {
  emerald: 'bg-emerald-500/10 text-emerald-700', accent: 'bg-accent/10 text-accent',
  violet: 'bg-violet-500/10 text-violet-700', amber: 'bg-amber-500/10 text-amber-700',
  blue: 'bg-blue-500/10 text-blue-700',
};
const Highlight = ({ icon: Icon, tone, label, item, onClick, showDur, showScore }) => {
  if (!item) return (
    <Card className="p-4 rounded-2xl opacity-60"><div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${TONE[tone]}`}><Icon className="h-3.5 w-3.5" />{label}</div><p className="text-sm text-muted-foreground mt-3">Not available</p></Card>
  );
  return (
    <Card className="p-4 rounded-2xl cursor-pointer hover:border-accent/50 transition-colors" onClick={() => onClick(item)} data-testid={`highlight-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${TONE[tone]}`}><Icon className="h-3.5 w-3.5" />{label}</div>
      <p className="font-semibold mt-2 text-sm">{item.origin_city} → {item.destination_city}</p>
      <p className="text-lg font-semibold tabular text-accent mt-0.5">{formatPrice(item.price, item.currency)}</p>
      <p className="text-[11px] text-muted-foreground">{showDur && item.duration_minutes ? `${Math.round(item.duration_minutes / 60)}h ${item.duration_minutes % 60}m · ` : ''}{showScore ? `score ${item.country_fare_score}/100 · ` : ''}{item.route}</p>
    </Card>
  );
};
